import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  boundingBoxesType,
  GetNumberType,
  GetResultDataItemType,
  GetResultDataType,
  GetStringType,
} from '../../../types/aiOcrResult';
import { orange50, orange_overlay30, green50 } from '../../../constants/colors';
import { PREVIEW_ZOOM } from '../../../constants/enum';
import ImageMagnifier from './ImageMagnifier';

const GREEN_OVERLAY = 'rgba(0, 163, 60, 0.2)';
import { ItemListType, ItemListChildType } from '../../../types/pattern';

interface OverlayProps {
  imageUrl: string;
  result: GetResultDataType;
  imageWidth: number;
  imageHeight: number;
  focusedField: string | null;
  forDemo?: boolean;
  currentPage?: number;
  itemList?: ItemListType[];
  showConfidence?: boolean;
  zoomLevel?: number;
  /** ルーペ（拡大表示）を有効にするか。デフォルトは有効 */
  magnifierEnabled?: boolean;
}

/**
 * フォーカス中の項目キーを枠に配る Context（#3275）。
 *
 * props で渡すと、値が変わるたびに枠の生成ループ（renderBoundingBoxes）を
 * 再実行する必要があり、入力欄をクリックするたびに 1,900個の枠を
 * 作り直すことになる。Context にすると生成ループは focusedField に
 * 依存しなくなり、購読している枠だけが再描画される。
 */
const FocusedFieldContext = React.createContext<string | null>(null);

interface BoundingBoxProps {
  boxes: boundingBoxesType[];
  color: string;
  focusedBgColor: string;
  /**
   * この枠に対応するフォーカスキー。Context の値と一致したらハイライトする。
   * 現在のフォーカス値は props ではなく Context から読むことで、
   * 枠の生成ループを focusedField に依存させずに済む（#3275）。
   */
  focusKey: string;
  tooltip?: string;
  containerWidth: number; // 表示領域の幅
  containerHeight: number; // 表示領域の高さ
  imageWidth: number; // 元の画像の幅
  imageHeight: number; // 元の画像の高さ
}

const BoundingBoxComponent: React.FC<BoundingBoxProps> = ({
  boxes,
  color,
  focusedBgColor,
  focusKey,
  tooltip,
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
}) => {
  const focusedField = React.useContext(FocusedFieldContext);
  const isFocused = focusedField === focusKey;
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFocused && boxRef.current) {
      boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isFocused]);

  if (boxes.length < 4) return null;

  const [topLeft, topRight, bottomRight, bottomLeft] = boxes;

  const expandPixels = 3; // 拡大したいピクセル数

  // スケーリング比率を計算
  const scaleX = containerWidth / imageWidth;
  const scaleY = containerHeight / imageHeight;
  const scale = Math.min(scaleX, scaleY);

  // 画像の実際の表示サイズを計算
  const displayWidth = imageWidth * scale;
  const displayHeight = imageHeight * scale;

  // 画像の表示位置のオフセットを計算（中央寄せの場合）
  const offsetX = (containerWidth - displayWidth) / 2;
  const offsetY = (containerHeight - displayHeight) / 2;

  // バウンディングボックスの座標を計算
  const scaledLeft =
    Math.max(0, Math.min(topLeft.x, bottomLeft.x) * scale - expandPixels) + offsetX;
  const scaledTop = Math.max(0, Math.min(topLeft.y, topRight.y) * scale - expandPixels) + offsetY;
  const scaledRight =
    Math.min(displayWidth, Math.max(topRight.x, bottomRight.x) * scale + expandPixels) + offsetX;
  const scaledBottom =
    Math.min(displayHeight, Math.max(bottomLeft.y, bottomRight.y) * scale + expandPixels) + offsetY;

  const width = scaledRight - scaledLeft;
  const height = scaledBottom - scaledTop;

  const box = (
    <div
      ref={boxRef}
      style={{
        position: 'absolute',
        left: `${scaledLeft}px`,
        top: `${scaledTop}px`,
        width: `${width}px`,
        height: `${height}px`,
        border: `1px solid ${color}`,
        backgroundColor: isFocused ? focusedBgColor : 'transparent',
        boxSizing: 'border-box',
        pointerEvents: tooltip ? 'auto' : 'none',
      }}
    />
  );

  // ツールチップが無いときは Tooltip で包まない（#3275）。
  // MUI の Tooltip は1個ごとに Popper・イベントリスナー・内部stateを持つため、
  // 座標枠が数千個ある帳票では包むだけで描画が固まる。
  // 中身（confidence 表示）は運営スタッフ限定なので、一般ユーザーでは
  // 全枠が「何も表示しない Tooltip」になっていた。枠の見た目は変わらない。
  if (!tooltip) return box;

  return (
    <Tooltip title={tooltip} arrow>
      {box}
    </Tooltip>
  );
};

/**
 * 枠は1ページに数百〜数千個並ぶため、親の再描画のたびに全枠を作り直すと重い（#3275）。
 * boxes は Firestore 由来の result 内の配列で参照が安定しており、
 * 他の props もプリミティブなので浅い比較で十分に効く。
 */
const BoundingBox = React.memo(BoundingBoxComponent);

/**
 * 枠を警告色にするか（#3262）。
 *
 * 要注意（needs_review）を優先して見る。バリデーション・照合スコア由来の
 * 判定も色に反映させるため、読み取り信頼度だけを見ていた従来の判定を置き換えている。
 * needs_review を持たない既存データでは is_uncertain にフォールバックする。
 *
 * 引数だけで決まるためコンポーネント外に置く（#3275。枠の生成を
 * useMemo 化するにあたり、依存を増やさないため）。
 */
const isFieldNeedsAttention = (fieldData: unknown): boolean => {
  if (fieldData === null || typeof fieldData !== 'object') return false;
  const field = fieldData as { needs_review?: boolean; is_uncertain?: boolean };
  if (field.needs_review !== undefined) return field.needs_review === true;
  return field.is_uncertain === true;
};

// フリーOCRの走査で子として辿らないメタキー（座標・id など）
const FREE_OCR_META_KEYS = new Set([
  'id',
  'label_id',
  'bounding_boxes',
  'label_bounding_boxes',
  'page',
  'label_page',
  'confidence',
  'no_label',
  'value',
  'ocr_value',
]);

/**
 * 項目名（ラベル）の枠に使う focusKey の接尾辞（#3315）。
 *
 * 値の枠とラベルの枠を別のキーにして、値をフォーカスしたら値だけ、
 * 項目名をフォーカスしたら項目名だけがハイライト・スクロールされるようにする。
 * FreeAiOcrResult 側の項目名欄の onFocus と同じ文字列を使うこと。
 */
export const FREE_OCR_LABEL_FOCUS_SUFFIX = '__label';

/**
 * フリーOCRのパスから focusKey を生成する（#3249）。
 * FreeAiOcrResult 側の buildFocusKey と同じ規則にする必要がある。
 * - 単一値: ['項目名'] → '項目名'
 * - 配列内: ['親項目名', 0, '子項目名'] → '親項目名-0-子項目名'
 * - かたまり内: ['送信先', 'FAX'] → '送信先.FAX'
 */
const buildFreeOcrFocusKey = (path: (string | number)[]): string => {
  if (path.length === 1) return String(path[0]);
  if (path.length === 3 && typeof path[1] === 'number') {
    return `${path[0]}-${path[1]}-${path[2]}`;
  }
  return path.join('.');
};

/** 読み取り信頼度のツールチップ本文（運営スタッフのみ）。同じく外に出す（#3275） */
const buildConfidenceTooltip = (
  fieldData: unknown,
  showConfidence: boolean,
): string | undefined => {
  if (!showConfidence) return undefined;
  const f = fieldData as { is_uncertain?: boolean; confidence?: number; avg_logprobs?: number };
  if (!f.is_uncertain) return undefined;
  const lines: string[] = [];
  if (f.confidence !== undefined) {
    lines.push(`Azure confidence: ${f.confidence.toFixed(3)}`);
  }
  if (f.avg_logprobs !== undefined) {
    lines.push(`Gemini avgLogprobs: ${f.avg_logprobs.toFixed(3)}`);
  }
  return lines.length > 0 ? lines.join('\n') : undefined;
};

const ImageOverlay: React.FC<OverlayProps> = ({
  imageUrl,
  result,
  imageWidth,
  imageHeight,
  focusedField,
  forDemo = false,
  currentPage,
  itemList = [],
  showConfidence = false,
  zoomLevel = PREVIEW_ZOOM.DEFAULT_LEVEL,
  magnifierEnabled = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [focusedBoundingBox, setFocusedBoundingBox] = useState<boundingBoxesType[] | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const rafIdRef = useRef<number | null>(null);

  // 項目名からIDを取得する関数
  const getItemIdByName = (itemName: string): string | null => {
    if (!itemList || itemList.length === 0) return null;
    const item = itemList.find(i => i.parentValue === itemName);
    return item ? String(item.id) : null;
  };

  // 子項目名からIDを取得する関数
  const getChildIdByName = (childName: string): string | null => {
    if (!itemList || itemList.length === 0) return null;
    for (const parent of itemList) {
      if (parent.children && parent.children.length > 0) {
        const child = parent.children.find((c: ItemListChildType) => c.childValue === childName);
        if (child) return String(child.id);
      }
    }
    return null;
  };

  useEffect(() => {
    const handleResize = (): void => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // zoomLevel 変更時にコンテナサイズを再取得（BB座標の再計算トリガー）
  // CSS幅変更後のレイアウト確定を待つため requestAnimationFrame を挟む
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [zoomLevel]);

  // unmount 時に rAF をクリーンアップ
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => {
      setMousePos({ x, y });
    });
  };

  const handleMouseLeave = (): void => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setMousePos(null);
  };

  const handleImageLoad = (): void => {
    requestAnimationFrame(() => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
      setImageLoaded(true);
    });
  };

  // フォーカスされたフィールドのバウンディングボックスを探す
  const findFocusedBoundingBox = (
    data: GetResultDataType,
    targetField: string,
  ): boundingBoxesType[] | null => {
    if (!targetField || !forDemo) return null;

    // 配列フィールドの場合（parentValue-itemIndex-childField format）
    const fieldParts = targetField.split('-');
    if (fieldParts.length === 3) {
      const [parentName, itemIndexStr, childName] = fieldParts;
      const itemIndex = parseInt(itemIndexStr, 10);

      // 項目名をIDに変換
      const parentId = getItemIdByName(parentName);
      const childId = getChildIdByName(childName);

      if (!parentId || !childId) return null;

      const parentFieldValue = data[parentId as keyof GetResultDataType];
      if (Array.isArray(parentFieldValue) && parentFieldValue[itemIndex]) {
        const item = parentFieldValue[itemIndex] as unknown as Record<string, unknown>;
        const childFieldValue = item[childId];
        if (
          childFieldValue &&
          typeof childFieldValue === 'object' &&
          'bounding_boxes' in childFieldValue &&
          Array.isArray((childFieldValue as { bounding_boxes?: unknown[] }).bounding_boxes)
        ) {
          return (childFieldValue as { bounding_boxes: boundingBoxesType[] }).bounding_boxes;
        }
      }
    } else {
      // 単一値フィールドの場合
      // 項目名をIDに変換
      const fieldId = getItemIdByName(targetField);
      if (!fieldId) return null;

      const fieldValue = data[fieldId as keyof GetResultDataType];
      if (
        fieldValue &&
        typeof fieldValue === 'object' &&
        'bounding_boxes' in fieldValue &&
        fieldValue.bounding_boxes
      ) {
        return fieldValue.bounding_boxes;
      }
    }

    return null;
  };

  // ページ番号でフィルタリングするヘルパー関数
  const isCurrentPageData = useCallback(
    (fieldData: unknown): boolean => {
      if (!currentPage || !fieldData) return true;

      // フィールドデータにページ番号があるかチェック
      if (typeof fieldData === 'object' && fieldData !== null && 'page' in fieldData) {
        const itemPage = (fieldData as { page: number | null | undefined }).page;
        // page が null/undefined（ページ不明）の項目は、どのページか判別できないため表示する。
        // ページ分割された result（1ページ1結果）では各項目の page が null になり、
        // page === currentPage で厳密比較すると全項目が除外されて bounding box が出なくなる。
        if (itemPage === null || itemPage === undefined) return true;
        return itemPage === currentPage;
      }

      return true; // ページ番号がない場合は表示
    },
    [currentPage],
  );

  // フォーカスされたフィールドが変更された時の処理
  useEffect(() => {
    if (imageLoaded && focusedField && containerSize.width > 0 && containerSize.height > 0) {
      const boundingBox = findFocusedBoundingBox(result, focusedField);
      if (boundingBox && boundingBox !== focusedBoundingBox) {
        setFocusedBoundingBox(boundingBox);
      }
    } else if (!focusedField) {
      setFocusedBoundingBox(null);
    }
  }, [focusedField, imageLoaded, containerSize, result, currentPage]);

  const buildTooltip = useCallback(
    (fieldData: unknown): string | undefined => buildConfidenceTooltip(fieldData, showConfidence),
    [showConfidence],
  );

  const renderBoundingBoxes = useCallback(
    (data: GetResultDataType): JSX.Element[] => {
      const boxes: JSX.Element[] = [];

      if (forDemo) {
        // 読取パターン用の動的フィールド処理
        // itemListがある場合はIDと項目名のマッピングを使用
        if (itemList && itemList.length > 0) {
          itemList.forEach(item => {
            const itemId = String(item.id);
            const itemName = item.parentValue;
            const fieldValue = data[itemId as keyof GetResultDataType];

            // 単一値フィールドの場合
            if (
              fieldValue &&
              typeof fieldValue === 'object' &&
              'bounding_boxes' in fieldValue &&
              fieldValue.bounding_boxes &&
              isCurrentPageData(fieldValue)
            ) {
              const isUncertain = isFieldNeedsAttention(fieldValue);
              boxes.push(
                <BoundingBox
                  key={itemId}
                  boxes={fieldValue.bounding_boxes}
                  color={isUncertain ? orange50 : green50}
                  focusedBgColor={isUncertain ? orange_overlay30 : GREEN_OVERLAY}
                  focusKey={itemName}
                  tooltip={buildTooltip(fieldValue)}
                  containerWidth={containerSize.width}
                  containerHeight={containerSize.height}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                />,
              );
            }

            // 配列フィールドの場合
            else if (Array.isArray(fieldValue)) {
              fieldValue.forEach((rowItem: unknown, itemIndex: number) => {
                const typedItem = rowItem as Record<string, unknown>;
                if (typedItem && typeof typedItem === 'object') {
                  // 子項目をループ
                  item.children.forEach((child: ItemListChildType) => {
                    const childId = String(child.id);
                    const childName = child.childValue;
                    const childFieldValue = typedItem[childId];

                    if (
                      childFieldValue &&
                      typeof childFieldValue === 'object' &&
                      'bounding_boxes' in childFieldValue &&
                      Array.isArray(
                        (childFieldValue as { bounding_boxes?: unknown[] }).bounding_boxes,
                      ) &&
                      isCurrentPageData(childFieldValue)
                    ) {
                      const focusKey = `${itemName}-${itemIndex}-${childName}`;
                      const isChildUncertain =
                        (childFieldValue as { is_uncertain?: boolean }).is_uncertain === true;
                      boxes.push(
                        <BoundingBox
                          key={focusKey}
                          boxes={
                            (childFieldValue as { bounding_boxes: boundingBoxesType[] })
                              .bounding_boxes
                          }
                          color={isChildUncertain ? orange50 : green50}
                          focusedBgColor={isChildUncertain ? orange_overlay30 : GREEN_OVERLAY}
                          focusKey={focusKey}
                          tooltip={buildTooltip(childFieldValue)}
                          containerWidth={containerSize.width}
                          containerHeight={containerSize.height}
                          imageWidth={imageWidth}
                          imageHeight={imageHeight}
                        />,
                      );
                    }
                  });
                }
              });
            }
          });
        } else {
          // itemListがない場合（フリーOCR）は result_data を再帰的に走査する。
          // フリーOCRはかたまり（セクション）でネストするため、第1階層だけでは枠が出ない（#3249）。
          const pushBoxesRecursively = (node: unknown, path: (string | number)[]): void => {
            if (!node || typeof node !== 'object') return;

            if (Array.isArray(node)) {
              node.forEach((item, itemIndex) => pushBoxesRecursively(item, [...path, itemIndex]));
              return;
            }

            const record = node as Record<string, unknown>;
            const focusKey = buildFreeOcrFocusKey(path);

            // 値の枠
            if (
              'bounding_boxes' in record &&
              Array.isArray(record.bounding_boxes) &&
              record.bounding_boxes.length > 0 &&
              isCurrentPageData(record)
            ) {
              const isUncertain = isFieldNeedsAttention(record);
              boxes.push(
                <BoundingBox
                  key={focusKey}
                  boxes={record.bounding_boxes as boundingBoxesType[]}
                  color={isUncertain ? orange50 : green50}
                  focusedBgColor={isUncertain ? orange_overlay30 : GREEN_OVERLAY}
                  focusKey={focusKey}
                  tooltip={buildTooltip(record)}
                  containerWidth={containerSize.width}
                  containerHeight={containerSize.height}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                />,
              );
            }

            // 項目名（ラベル）の枠。値とは別の focusKey にする（#3315）。
            // 同じキーにすると値・ラベルの両方がハイライトされ、さらに双方が
            // scrollIntoView を呼ぶため、明細のように列見出しが全行で共通だと
            // どの行の値を押しても見出しへ飛んでしまう。
            // 値ノード（value を持つ）のみ対象。かたまり自体には項目名の枠を出さない。
            if (
              'value' in record &&
              Array.isArray(record.label_bounding_boxes) &&
              record.label_bounding_boxes.length > 0 &&
              isCurrentPageData({ page: record.label_page })
            ) {
              boxes.push(
                <BoundingBox
                  key={`${focusKey}${FREE_OCR_LABEL_FOCUS_SUFFIX}`}
                  boxes={record.label_bounding_boxes as boundingBoxesType[]}
                  color={green50}
                  focusedBgColor={GREEN_OVERLAY}
                  focusKey={`${focusKey}${FREE_OCR_LABEL_FOCUS_SUFFIX}`}
                  containerWidth={containerSize.width}
                  containerHeight={containerSize.height}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                />,
              );
            }

            // 値ノードの内部（value や座標情報）は走査しない。
            // それ以外はかたまり・配列なので子を辿る。
            if ('value' in record) return;
            Object.entries(record).forEach(([key, value]) => {
              if (FREE_OCR_META_KEYS.has(key)) return;
              pushBoxesRecursively(value, [...path, key]);
            });
          };

          pushBoxesRecursively(data, []);
        }
      } else {
        // 従来の固定フィールド処理
        const singleFields = [
          'client_name',
          'post_code',
          'address',
          'invoice_number',
          'invoice_date',
          'total_amount',
        ] as const;

        singleFields.forEach(field => {
          if (data[field] && data[field].bounding_boxes) {
            const isUncertain = isFieldNeedsAttention(data[field]);
            boxes.push(
              <BoundingBox
                key={field}
                boxes={data[field].bounding_boxes}
                color={isUncertain ? orange50 : green50}
                focusedBgColor={isUncertain ? orange_overlay30 : GREEN_OVERLAY}
                focusKey={field}
                containerWidth={containerSize.width}
                containerHeight={containerSize.height}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
              />,
            );
          }
        });

        data.list?.forEach((item: GetResultDataItemType, index: number) => {
          Object.keys(item).forEach(field => {
            const fieldValue = item[field as keyof GetResultDataItemType] as
              | GetStringType
              | GetNumberType;
            if (fieldValue.bounding_boxes) {
              const isUncertain = isFieldNeedsAttention(fieldValue);
              boxes.push(
                <BoundingBox
                  key={`${index}-${field}`}
                  boxes={fieldValue.bounding_boxes}
                  color={isUncertain ? orange50 : green50}
                  focusedBgColor={isUncertain ? orange_overlay30 : GREEN_OVERLAY}
                  focusKey={`${index}-${field}`}
                  containerWidth={containerSize.width}
                  containerHeight={containerSize.height}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                />,
              );
            }
          });
        });
      }
      return boxes;
    },
    [forDemo, itemList, isCurrentPageData, buildTooltip, containerSize, imageWidth, imageHeight],
  );

  /**
   * 枠の要素生成をメモ化する（#3275）。
   *
   * 入力欄をクリックすると親の focusItem が変わり、この ImageOverlay も
   * 再レンダーされる。メモ化しないと、そのたびに 1,900個規模の枠を
   * 作り直すことになり、フォーカスの反応が数秒単位で遅れていた。
   *
   * ハイライトの切り替えは BoundingBox 側が focusKey と Context の値を
   * 比較して行うため、ここでは focusedField を依存に含めない。
   * その結果、フォーカス変更で再描画されるのは該当する枠だけになる。
   *
   * renderBoundingBoxes は毎レンダー再生成されるので依存に入れず、
   * 実際に参照している値だけを列挙している。
   */
  const boundingBoxElements = useMemo(
    () => renderBoundingBoxes(result),
    [renderBoundingBoxes, result],
  );

  // ルーペに渡す、実際に表示されている画像サイズとオフセットを計算
  // BoundingBox 内の scale 計算と同じロジック
  const fitScaleX = containerSize.width > 0 ? containerSize.width / imageWidth : 0;
  const fitScaleY = containerSize.height > 0 ? containerSize.height / imageHeight : 0;
  const fitScale = Math.min(fitScaleX, fitScaleY);
  const displayedWidth = imageWidth * fitScale;
  const displayedHeight = imageHeight * fitScale;
  const imageOffsetX = (containerSize.width - displayedWidth) / 2;
  const imageOffsetY = (containerSize.height - displayedHeight) / 2;

  return (
    <Box
      ref={containerRef}
      position="relative"
      sx={{
        width: `${zoomLevel * PREVIEW_ZOOM.PERCENT_BASE}%`,
        flexShrink: 0,
      }}
      data-page-number={currentPage}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        component="img"
        src={imageUrl}
        alt="Invoice"
        onLoad={handleImageLoad}
        sx={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {/* focusedField は Context で配る（#3275）。
            boundingBoxElements 自体は focusedField に依存しないため、
            フォーカス変更で作り直されるのは該当する枠だけになる */}
        <FocusedFieldContext.Provider value={focusedField}>
          {imageLoaded && boundingBoxElements}
        </FocusedFieldContext.Provider>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: 6,
          left: 8,
          display: 'flex',
          gap: 1.5,
          pointerEvents: 'none',
        }}
      >
        {[
          { color: green50, label: '正常' },
          { color: orange50, label: '確認推奨' },
        ].map(({ color, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                border: `2px solid ${color}`,
                borderRadius: '2px',
                flexShrink: 0,
              }}
            />
            <Box component="span" sx={{ fontSize: '10px', color: 'text.secondary', lineHeight: 1 }}>
              {label}
            </Box>
          </Box>
        ))}
      </Box>
      {imageLoaded && magnifierEnabled && mousePos && displayedWidth > 0 && displayedHeight > 0 && (
        <ImageMagnifier
          imageUrl={imageUrl}
          displayedWidth={displayedWidth}
          displayedHeight={displayedHeight}
          imageOffsetX={imageOffsetX}
          imageOffsetY={imageOffsetY}
          mouseX={mousePos.x}
          mouseY={mousePos.y}
        />
      )}
    </Box>
  );
};

/**
 * 結果画面（#3275）では明細セルへの1文字入力ごとに親が再レンダーされ、
 * ページ数ぶんの ImageOverlay が bounding box を作り直していた。
 * props が変わらない限り再描画しないようメモ化する。
 *
 * 前提: 呼び出し側は result / itemList を安定した参照で渡すこと
 * （JSX 内で filter や sort を呼ぶと毎回新しい配列になり、メモ化が効かない）。
 */
export default React.memo(ImageOverlay);
